import { observable, computed, action } from "mobx";
import StrapiHelper from "../services/StrapiHelper.service";
import Swal from "../services/swalCompat";

export default class UserStore {
  @observable
  currentUser: any;

  @observable
  showHomeModal: boolean = true;

  navigation: any;

  @observable
  loading: any = false;

  @observable
  error: any = "";

  // Flag to prevent multiple validation calls
  private isValidating: boolean = false;

  constructor() {
    // Load user from localStorage immediately
    if (window.localStorage.getItem("user")) {
      var localUser = window.localStorage.getItem("user")!;
      this.currentUser = JSON.parse(localUser);

      // Validate token and refresh user data in background
      this.validateAndRefreshUser();
    }
  }

  @action
  async validateAndRefreshUser() {
    // Prevent multiple validation calls
    if (this.isValidating) {
      return;
    }

    const token = localStorage.getItem("mohren_token");

    // If no token, clear everything
    if (!token) {
      this.logout();
      return;
    }

    this.isValidating = true;

    try {
      // Validate token by fetching current user from server
      const response = await StrapiHelper.getCurrentUser();
      if (response.status === 200) {
        let userWithoutCodes = { ...response.data };
        delete userWithoutCodes.codes;

        // Update both store and localStorage with fresh data
        this.currentUser = userWithoutCodes;
        this.updateUserLocal(userWithoutCodes);

        console.log("✅ User session validated and refreshed");
      }
    } catch (error) {
      console.warn("❌ Token validation failed, logging out:", error);
      // Token is invalid, clear everything
      this.logout();
    } finally {
      this.isValidating = false;
    }
  }

  @action
  async login(email: string, password) {
    try {
      const response = await StrapiHelper.emailLogin(email, password);
      if (response.status === 200) {
        const { jwt, user } = response.data;
        let userWithoutCodes = { ...user };
        delete userWithoutCodes.codes; // Remove the codes field

        localStorage.setItem("mohren_token", jwt);
        localStorage.setItem("user", JSON.stringify(userWithoutCodes));
        this.currentUser = userWithoutCodes;
        return true;
      }
    } catch (err) {
      const errAny = err as any;

      if (
        errAny.response &&
        errAny.response.data &&
        errAny.response.data.message &&
        errAny.response.data.message[0] &&
        errAny.response.data.message[0].messages &&
        errAny.response.data.message[0].messages[0] &&
        errAny.response.data.message[0].messages[0].id ===
          "Auth.form.error.confirmed"
      ) {
        const result = await Swal.fire({
          title: "Bitte bestätige deine Email-Adresse",
          text:
            "Wir haben dir einen Bestätigungslink an " +
            email +
            " gesendet. Hast du keine Mail erhalten? Schau auch im Spam-Ordner nach oder fordere die Bestätigungs-Mail erneut an.",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#ffd42e",
          confirmButtonText: "Bestätigung erneut senden",
          cancelButtonText: "Schließen",
          reverseButtons: true,
        });

        if (result.value) {
          // Client-side cooldown so a frustrated user can't hammer the button
          // before the server-side rate limiter (10/h per IP, 3/h per email)
          // kicks in. Stored per-email so switching accounts resets it.
          const cooldownKey = "resend_confirm_cooldown:" + email.toLowerCase().trim();
          const lastSentAt = parseInt(localStorage.getItem(cooldownKey) || "0", 10);
          const cooldownMs = 60 * 1000;
          const remainingMs = lastSentAt + cooldownMs - Date.now();
          if (remainingMs > 0) {
            Swal.fire({
              text:
                "Bitte warte noch " +
                Math.ceil(remainingMs / 1000) +
                " Sekunden, bevor du eine neue Bestätigungs-Mail anforderst.",
              type: "info",
              confirmButtonColor: "#ffd42e",
              confirmButtonText: "Ok",
            });
            return false;
          }
          try {
            localStorage.setItem(cooldownKey, String(Date.now()));
            await StrapiHelper.resendEmailConfirmation(email);
            Swal.fire({
              text:
                "Bestätigungs-Mail wurde erneut versendet an " +
                email +
                ". Bitte prüfe auch deinen Spam-Ordner.",
              type: "success",
              confirmButtonColor: "#ffd42e",
              confirmButtonText: "Ok",
            });
          } catch (resendErr) {
            // Roll back the cooldown stamp so the user can retry on transient
            // failures, but NOT on 429 (server told us to back off).
            const status =
              (resendErr as any)?.response?.status;
            if (status !== 429) {
              localStorage.removeItem(cooldownKey);
            }
            Swal.fire({
              text:
                status === 429
                  ? "Zu viele Anfragen. Bitte warte ca. eine Stunde, bevor du es erneut versuchst."
                  : "Mail konnte nicht erneut gesendet werden. Bitte versuche es später noch einmal.",
              type: "error",
              confirmButtonColor: "#ffd42e",
              confirmButtonText: "Ok",
            });
          }
        }

        return false;
      }
      console.log(err);
      Swal.fire({
        text: "Deine Email oder dein Passwort sind inkorrekt!",
        type: "error",
        confirmButtonColor: "#ffd42e",
        confirmButtonText: "Ok",
      });
      return false;
    }
  }

  @action
  async loginWithFacebook(token: string) {
    try {
      const response = await StrapiHelper.loginWithFB(token);

      if (response.status === 200) {
        const { jwt, user } = response.data;
        let userWithoutCodes = { ...user };
        delete userWithoutCodes.codes; // Remove the codes field

        localStorage.setItem("mohren_token", jwt);
        localStorage.setItem("user", JSON.stringify(userWithoutCodes));
        this.currentUser = userWithoutCodes;
        return true;
      }
    } catch (err) {
      console.log(err);

      return false;
    }
  }

  @action
  async loginWithApple(token: string) {
    try {
      const response = await StrapiHelper.loginWithApple(token);

      if (response.status === 200) {
        const { jwt, user } = response.data;

        let userWithoutCodes = { ...user };
        delete userWithoutCodes.codes; // Remove the codes field

        localStorage.setItem("mohren_token", jwt);
        localStorage.setItem("user", JSON.stringify(userWithoutCodes));
        this.currentUser = userWithoutCodes;
        return true;
      }
    } catch (err) {
      console.log(err);

      return false;
    }
  }

  @action
  async register(email, password) {
    try {
      const response = await StrapiHelper.emailRegister(email, password);
      if (response.status === 200) {
        if (this.navigation) {
          this.navigation.push("emailregistrationdone");
        }
      }
    } catch (err) {
      let errAny = err as any;
      if (
        errAny.response &&
        errAny.response.data &&
        errAny.response.data.message &&
        errAny.response.data.message[0] &&
        errAny.response.data.message[0].messages &&
        errAny.response.data.message[0].messages[0] &&
        errAny.response.data.message[0].messages[0].id ===
          "Auth.form.error.email.taken"
      ) {
        Swal.fire({
          text: "Diese Email wird schon verwendet!",
          type: "error",
          confirmButtonColor: "#ffd42e",
          confirmButtonText: "Ok",
        });

        return false;
      }
      console.log(err);
      Swal.fire({
        text: "Deine Email oder dein Passwort sind inkorrekt!",
        type: "error",
        confirmButtonColor: "#ffd42e",
        confirmButtonText: "Ok",
      });
      return false;
    }
  }

  @action
  async setTutorialCompleted(completed: boolean) {
    let userObj = this.currentUser;
    userObj["aktion24TutorialCompleted"] = completed;
    userObj["tutorialCompleted"] = completed;
    this.updateUser(userObj);
  }

  @action setAktion24TutorialCompleted(completed: boolean) {
    let userObj = this.currentUser;
    userObj["aktion24TutorialCompleted"] = completed;
    this.updateUser(userObj);
  }

  @action
  async setAgbChecked(checked: boolean) {
    let userObj = this.currentUser;
    userObj["agbChecked"] = checked;

    this.currentUser = userObj;

    // Persist to backend
    await this.updateUser(userObj);
  }

  @action
  setUsername(username: any) {
    let userObj = this.currentUser;
    userObj["username"] = username;
    this.currentUser = userObj;
  }

  @action
  stopLogin() {
    this.loading = false;
  }

  @action
  startLogin() {
    this.loading = true;
  }

  @action
  homeModalClosed() {
    this.showHomeModal = false;
  }

  @action
  updateUserLocal(user) {
    let jsonUser = JSON.stringify(user);

    if (jsonUser) {
      localStorage.removeItem("user");
      localStorage.setItem("user", jsonUser);
    }
  }

  @action
  async getCurrentUser() {
    try {
      let response = await StrapiHelper.getCurrentUser();
      let userWithoutCodes = { ...response.data };
      delete userWithoutCodes.codes; // Remove the codes field

      this.currentUser = userWithoutCodes;
      this.updateUserLocal(userWithoutCodes);
    } catch (err) {}
  }

  @action
  async updateUser(user) {
    const tmpUser = JSON.parse(JSON.stringify(user));
    for (let key in tmpUser) {
      if (
        tmpUser[key] == null ||
        key === "codes" ||
        key === "logs" ||
        key === "points" ||
        key === "username" ||
        key === "totalPoints" ||
        key === "tickets"
      ) {
        delete tmpUser[key];
      }
    }

    try {
      const response = await StrapiHelper.updateUser(tmpUser);
      if (response.status == 200) {
        let userWithoutCodes = { ...user };
        delete userWithoutCodes.codes; // Remove the codes field

        this.updateUserLocal(userWithoutCodes);
        this.currentUser = userWithoutCodes;

        this.loading = false;
      } else {
        this.error = "Error";
        this.loading = false;
      }
      return response;
    } catch (err) {
      console.log(err);
      Swal.fire({
        text: "Speichern hat nicht funktioniert!",
        type: "error",
        confirmButtonColor: "#ffd42e",
        confirmButtonText: "Ok",
      });

      this.loading = false;
      return err;
    }
  }

  @action
  async updateUsername(user, userName?) {
    for (let key in user) {
      if (
        user[key] == null ||
        key === "codes" ||
        key === "logs" ||
        key === "points" ||
        key === "totalPoints" ||
        key === "tickets"
      ) {
        delete user[key];
      }
    }

    // try {
    const response = await StrapiHelper.updateUser(user);
    if (response.status == 200) {
      let userWithoutCodes = { ...response.data };
      delete userWithoutCodes.codes; // Remove the codes field

      this.updateUserLocal(userWithoutCodes);
      this.currentUser = userWithoutCodes;
    }
    return response;
  }

  @action
  async updateUserImage(image) {
    let userObj = this.currentUser;
    userObj["image"] = image;
    await this.updateUser(userObj);
  }

  @action
  async addPoints(points: any) {
    let userObj = this.currentUser;
    userObj["points"] = points;
    await this.updateUserLocal(userObj);
  }

  @action
  async addTickets(tickets: any) {
    let userObj = this.currentUser;
    userObj["tickets"] = tickets;
    await this.updateUserLocal(userObj);
  }

  @action
  async setSeenDialogs(seenDialogs) {
    let userObj = this.currentUser;
    userObj["seenDialogs"] = seenDialogs;

    for (let key in userObj) {
      if (
        userObj[key] == null ||
        key === "codes" ||
        key === "logs" ||
        key === "points" ||
        key === "totalPoints" ||
        key === "tickets"
      ) {
        delete userObj[key];
      }
    }

    const response = await StrapiHelper.updateUser(userObj);
    if (response.status == 200) {
      let userWithoutCodes = { ...response.data };
      delete userWithoutCodes.codes;

      this.updateUserLocal(userWithoutCodes);
      this.currentUser = userWithoutCodes;
    }
    return response;
  }

  @action
  async setWatchedStories(stories) {
    let userObj = this.currentUser;
    userObj["watchedStories"] = stories;

    for (let key in userObj) {
      if (
        userObj[key] == null ||
        key === "codes" ||
        key === "logs" ||
        key === "points" ||
        key === "totalPoints" ||
        key === "tickets"
      ) {
        delete userObj[key];
      }
    }

    // try {
    const response = await StrapiHelper.updateUser(userObj);
    if (response.status == 200) {
      let userWithoutCodes = { ...response.data };
      delete userWithoutCodes.codes; // Remove the codes field

      this.updateUserLocal(userWithoutCodes);
      this.currentUser = userWithoutCodes;
    }
    return response;
  }

  @action
  logout() {
    localStorage.removeItem("ShoppingCart");
    localStorage.removeItem("watched_stories");
    localStorage.removeItem("user");
    localStorage.removeItem("mohren_token");

    // Clear the current user from store
    this.currentUser = null;

    console.log("🚪 User logged out and session cleared");
  }

  @action
  forgotPassword(email: string) {
    this.startLogin();
    return StrapiHelper.forgotMyPassword(email);
  }

  @action
  async deleteAccount() {
    await StrapiHelper.deleteUser()
      .then((result) => {
        this.logout();
      })
      .catch((err) => {});
  }

  @action
  async resetPassword(code, password, repeatedPassword) {
    try {
      const response = await StrapiHelper.resetPassword(
        code,
        password,
        repeatedPassword
      );
      if (response && response.status === 200) {
        const { jwt, user } = response.data;

        let userWithoutCodes = { ...user };
        delete userWithoutCodes.codes; // Remove the codes field

        localStorage.setItem("mohren_token", jwt);
        localStorage.setItem("user", JSON.stringify(userWithoutCodes));
        this.currentUser = userWithoutCodes;
        return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  }
}
